import { type Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { balanceSheetsQueryKey } from "./financials.keys";
import { patchBalanceSheetView } from "./utils/patchBalanceSheetView";

export function useCreateBalanceSheetLineItem(accountId: SuretyAccountId) {
	const { post } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["createBalanceSheetLineItem", accountId],
		mutationFn: (body: Dtos.Admin.BalanceSheet.CreateLineItem.Request) =>
			post(`/v2/admin/surety/accounts/${accountId}/balance-sheets/line-items`, body, "none"),
		onMutate: async (body) => {
			await queryClient.cancelQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
			);
			const periodByBalanceSheetId = new Map(
				body.periods.map((period) => [period.balanceSheetId, period]),
			);
			queryClient.setQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
				(cached) =>
					cached?.map((sheet) => {
						const period = periodByBalanceSheetId.get(sheet.id);
						return period !== undefined && sheet.view
							? {
									...sheet,
									view: patchBalanceSheetView(sheet.view, {
										sectionPath: body.sectionPath,
										label: body.label,
										values: { value: period.value, formula: period.formula },
									}),
								}
							: sheet;
					}),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(balanceSheetsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (queryClient.isMutating({ mutationKey: ["createBalanceSheetLineItem", accountId] }) <= 1) {
				queryClient.invalidateQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			}
		},
	});

	return {
		createBalanceSheetLineItem: mutation.mutateAsync,
	};
}
